import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loader } from "@/components/Loader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/lib/format";

export default function Notifications() {
  const qc = useQueryClient();
  const { data: notes = [], isLoading } = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data || [] });
  const readMut = useMutation({
    mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Notifications" subtitle="Alerts and updates" testId="page-notifications" />
      {isLoading ? <Loader /> : notes.length === 0 ? (
        <EmptyState icon={Bell} title="You're all caught up" description="New alerts about appointments, labs and stock will appear here." testId="notifications-empty" />
      ) : (
        <div className="space-y-2">
          {notes.map((n, i) => (
            <div key={n.id || i} data-testid={`notification-${i}`} className={`flex items-start gap-3 rounded-xl border bg-white p-4 shadow-sm ${n.read ? "border-slate-200" : "border-teal-200"}`}>
              <div className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg ${n.read ? "bg-slate-100 text-slate-400" : "bg-teal-50 text-teal-700"}`}><Bell className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800">{n.title || n.message || "Notification"}</p>
                {n.body && <p className="text-sm text-slate-500">{n.body}</p>}
                <p className="mt-0.5 text-xs text-slate-400">{timeAgo(n.created_at)}</p>
              </div>
              {!n.read && <Button size="sm" variant="ghost" data-testid={`mark-read-${i}`} onClick={() => readMut.mutate(n.id)}><Check className="h-4 w-4" /></Button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
